import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import Swal from 'sweetalert2';
import { TrendingUp, Save, BarChart3, Users, Droplets, Zap, Globe } from 'lucide-react';

const AdminGrowthStats = () => {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const docRef = doc(db, 'site_content', 'stats');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists() && docSnap.data().items) {
          setStats(docSnap.data().items);
        } else {
          setStats([]);
        }
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const handleChange = (index, field, value) => {
    setStats(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    Swal.fire({
      title: 'Saving Growth Stats...',
      text: 'Updating home page statistics',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const docRef = doc(db, 'site_content', 'stats');
      await setDoc(docRef, { items: stats });

      Swal.fire({
        title: 'Success!',
        text: 'Growth stats updated successfully.',
        icon: 'success',
        confirmButtonColor: '#0ea732'
      });
    } catch (error) {
      console.error("Error saving stats:", error);
      Swal.fire('Error', 'Failed to update growth stats', 'error');
    } finally {
      setSaving(false);
    }
  };

  const getStatIcon = (idx) => {
    switch (idx) {
      case 0: return <Users className="text-sugarcane-green" size={20} />;
      case 1: return <BarChart3 className="text-sugarcane-green" size={20} />;
      case 2: return <Droplets className="text-sugarcane-green" size={20} />;
      case 3: return <Zap className="text-sugarcane-green" size={20} />;
      case 4: return <Globe className="text-sugarcane-green" size={20} />;
      default: return <TrendingUp className="text-sugarcane-green" size={20} />;
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sugarcane-green"></div>
        <p className="mt-4 text-gray-500 font-medium">Loading Growth Stats...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-3xl border border-gray-100 shadow-premium p-8 max-w-5xl mx-auto font-poppins">
      <div className="flex items-center justify-between pb-6 mb-8 border-b border-gray-100">
        <div>
          <h3 className="text-2xl font-extrabold text-sugarcane-charcoal flex items-center gap-3">
            <div className="w-10 h-10 bg-sugarcane-green/10 rounded-xl flex items-center justify-center">
              <TrendingUp className="text-sugarcane-green" size={22} />
            </div>
            Manage Growth Stats
          </h3>
          <p className="text-gray-400 text-xs mt-1">Customize the 5 growth statistic cards displayed prominently on the Home page.</p>
        </div>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {stats.map((stat, idx) => (
            <div key={idx} className="p-6 bg-gray-50 rounded-2xl border border-gray-100 space-y-4 hover:border-sugarcane-green/30 transition-all">
              <div className="flex items-center justify-between">
                <span className="text-xs font-black text-sugarcane-green uppercase tracking-widest flex items-center gap-2">
                  {getStatIcon(idx)}
                  Stat Card {idx + 1}
                </span>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Title / Label</label>
                <input
                  type="text"
                  value={stat.label || ''}
                  onChange={(e) => handleChange(idx, 'label', e.target.value)}
                  className="w-full bg-white border border-gray-200 text-sugarcane-charcoal rounded-xl py-3 px-4 focus:outline-none focus:ring-2 focus:ring-sugarcane-green text-sm font-bold"
                  placeholder="e.g. Farmers Connected"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Number / Value</label>
                  <input
                    type="number"
                    value={stat.value !== undefined ? stat.value : ''}
                    onChange={(e) => handleChange(idx, 'value', Number(e.target.value))}
                    className="w-full bg-white border border-gray-200 text-sugarcane-charcoal rounded-xl py-3 px-4 focus:outline-none focus:ring-2 focus:ring-sugarcane-green text-sm font-bold"
                    placeholder="e.g. 25000"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Suffix (Optional)</label>
                  <input
                    type="text"
                    value={stat.suffix !== undefined ? stat.suffix : ''}
                    onChange={(e) => handleChange(idx, 'suffix', e.target.value)}
                    className="w-full bg-white border border-gray-200 text-sugarcane-charcoal rounded-xl py-3 px-4 focus:outline-none focus:ring-2 focus:ring-sugarcane-green text-sm font-bold"
                    placeholder="e.g. + or K or MW"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end pt-6 border-t border-gray-100">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center space-x-2 bg-sugarcane-green text-white hover:bg-forest px-8 py-4 rounded-xl font-bold tracking-wide text-sm transition-all duration-300 shadow-md shadow-sugarcane-green/10 disabled:opacity-55 cursor-pointer"
          >
            <Save size={18} />
            <span>{saving ? 'Saving Stats...' : 'Save Growth Stats'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminGrowthStats;
